import * as React from 'react';

import { Point, ResponsiveLine, Serie } from '@nivo/line';
import * as d3 from 'd3-shape';
import usePqTransform from 'hooks/usePqTransform';
import { useTranslation } from 'react-i18next';
import { cleanUnderscore } from 'utils/afsUtils';

import {
  interpPoint,
  loessSmooth,
  mapJsonB,
  maxX,
} from '../../../../services/utils/tuningPlotUtils';

interface ChartProps {
  refFlavorTuningData: reports.RefFlavorTuningPlotRow[];
  decompData: reports.ReportFpDecompRow[];
  ggVar: constants.GGVar;
  type: 'decomp' | 'absDecomp';
  minPq?: number;
  maxPq?: number;
}

const refFlavorChartColors = [
  '#fbb4af',
  '#cd9600',
  '#7cae00',
  '#00be67',
  '#00bfc4',
  '#2b5469',
  '#c77cff',
  '#ff61cc',
];

const circle =
  (referenceFlavorSerieses: reports.NivoDataSeries[]) =>
  ({ data, points, ...info }) => {
    const currentValues = referenceFlavorSerieses.map(
      (series: reports.NivoDataSeries) => ({
        serieId: series.id,
        currentValue: series.currentValue,
        color: series.color,
      }),
    );

    const middlePoints = currentValues
      .map((currentValue) => ({
        mp: interpPoint(
          points.filter((p: Point) => p.serieId === currentValue.serieId),
          currentValue.currentValue.x,
        ),
        color: currentValue.color,
      }))
      .filter((middlePoint) => !!middlePoint.mp);

    const dots: JSX.Element[] = middlePoints.map((middlePoint) => (
      <path
        d={d3.symbol(d3.symbolCircle)()}
        transform={`translate(${middlePoint.mp.x} ${middlePoint.mp.y})`}
        color={middlePoint.color}
        fill={middlePoint.color}
      />
    ));

    return dots;
  };

const RefFlavorIntensityPqChart: React.FC<ChartProps> = (props) => {
  const { refFlavorTuningData, decompData, ggVar, type, minPq, maxPq } = props;
  const { t } = useTranslation();
  const pq = usePqTransform();

  if (refFlavorTuningData?.length == 0) {
    return <div />;
  }

  const mapData = (row: reports.RefFlavorTuningPlotRow): reports.NivoData[] => {
    switch (type) {
      case 'decomp':
        return mapJsonB(row.decompJsonb);
      case 'absDecomp':
        return mapJsonB(row.absDecompJsonb);
      default:
        return [];
    }
  };

  const mapRowToChartData = (
    row: reports.RefFlavorTuningPlotRow,
    decompData: reports.ReportFpDecompRow[],
  ): reports.NivoDataSeries => {
    return {
      id: cleanUnderscore(row.referenceFlavor),
      data: mapData(row),
      currentValue: mapCurrentValue(row, decompData),
    };
  };

  const mapCurrentValue = (
    row: reports.RefFlavorTuningPlotRow,
    decompData: reports.ReportFpDecompRow[],
  ): reports.NivoData => {
    const matchingDecomp = decompData.find(
      (d) => d.referenceFlavor === row.referenceFlavor,
    );
    if (!matchingDecomp) {
      return {
        x: 0,
        y: Number(refFlavorTuningData.find(Boolean).currentPq),
      };
    } else {
      return {
        x: Number(
          type === 'decomp'
            ? Number(matchingDecomp?.decomp)
            : Number(matchingDecomp?.absDecomp),
        ),
        y: Number(refFlavorTuningData.find(Boolean).currentPq),
      };
    }
  };

  const bottomTitle: () => string = () => {
    switch (type) {
      case 'decomp':
        return 'Signature Decomp';
      case 'absDecomp':
        return 'Signature Absolute Decomp';
      default:
        return '';
    }
  };

  const byPeakToPeak = (
    a: reports.NivoDataSeries,
    b: reports.NivoDataSeries,
  ) => {
    const aP2P =
      Math.max(...a.data.map((d) => d.y)) - Math.min(...a.data.map((d) => d.y));
    const bP2P =
      Math.max(...b.data.map((d) => d.y)) - Math.min(...b.data.map((d) => d.y));
    return bP2P - aP2P;
  };

  const alpha = (a: reports.NivoDataSeries, b: reports.NivoDataSeries) => {
    return b.id.localeCompare(a.id);
  };

  // Take the tuning data and map it to chart data, but filter out potentially junk values
  const chartData: reports.NivoDataSeries[] = refFlavorTuningData
    .map((row) => mapRowToChartData(row, decompData))
    .filter(
      (s) => !Number.isNaN(s.currentValue.x) && !Number.isNaN(s.currentValue.y),
    )
    .sort(byPeakToPeak)
    .slice(0, 8)
    .sort(alpha)
    .map((series, i) => ({
      id: series.id,
      currentValue: series.currentValue,
      color: refFlavorChartColors[i],
      data: loessSmooth(series.data, 31, 10, series.currentValue),
    }));

  return (
    <div
      key={
        refFlavorTuningData.find(Boolean).reportId +
        refFlavorTuningData.find(Boolean).productId?.toString() +
        ggVar.key +
        type
      }
      style={{ height: '300px' }}
    >
      <ResponsiveLine
        data={chartData as Serie[]}
        margin={{ top: 50, right: 125, bottom: 50, left: 60 }}
        curve="catmullRom" // Make the curve
        markers={[
          {
            axis: 'y', // axis the marker should be perpendicular to
            value: refFlavorTuningData.find(Boolean).currentPq, // value of the marker in axis
            lineStyle: {
              stroke: 'red',
              strokeWidth: 1,
              strokeDasharray: '12, 6', // Make line dashed
            },
          },
        ]}
        xScale={{
          type: 'linear',
          min: -0.001,
          max: maxX(chartData),
        }}
        yScale={{
          type: 'linear',
          min: minPq || 'auto',
          max: maxPq || 'auto',
        }}
        yFormat=" >-.2f"
        axisTop={null}
        axisRight={null}
        layers={[
          'grid',
          'markers',
          'axes',
          'areas',
          'crosshair',
          'lines',
          'points',
          'slices',
          'mesh',
          'legends',
          circle(chartData),
        ]}
        axisBottom={{
          tickSize: 5,
          tickPadding: 5,
          tickRotation: 0,
          legend: bottomTitle(),
          legendOffset: 36,
          legendPosition: 'middle',
          tickValues: 6,
          format: (v) => {
            switch (type) {
              case 'decomp':
                return `${Math.round(v * 1000) / 10}%`;
              case 'absDecomp':
                return `${v}`;
              default:
                return '';
            }
          },
        }}
        axisLeft={{
          tickSize: 5,
          tickPadding: 5,
          tickRotation: 0,
          legend: pq.label,
          legendOffset: -40,
          legendPosition: 'middle',
          tickValues: 5,
        }}
        colors={chartData.map((s) => s.color)}
        lineWidth={2}
        enableSlices={'x'}
        enableCrosshair={true}
        enablePoints={false}
        pointSize={6}
        pointColor={{ from: 'color' }}
        pointBorderWidth={2}
        pointBorderColor={{ from: 'serieColor' }}
        pointLabel="y"
        pointLabelYOffset={-12}
        useMesh={true}
        legends={[
          {
            anchor: 'right',
            direction: 'column',
            toggleSerie: true,
            justify: false,
            translateX: 120,
            translateY: 0,
            itemsSpacing: 0,
            itemDirection: 'left-to-right',
            itemWidth: 120,
            itemHeight: 20,
            itemOpacity: 0.75,
            symbolSize: 12,
            symbolShape: 'circle',
            symbolBorderColor: 'rgba(0, 0, 0, .5)',
            effects: [
              {
                on: 'hover',
                style: {
                  itemBackground: 'rgba(0, 0, 0, .03)',
                  itemOpacity: 1,
                },
              },
            ],
          },
        ]}
      />
    </div>
  );
};

export default RefFlavorIntensityPqChart;
